import type { Config } from 'tailwindcss';
import defaultTheme from 'tailwindcss/defaultTheme';
import plugin from 'tailwindcss/plugin';

const { fontFamily } = defaultTheme;
fontFamily.sans = ['Plus Jakarta Sans', 'sans-serif'];

export default {
  content: ['./index.html', './src/**/*.{js,ts,jsx,tsx}'],
  theme: {
    fontFamily,
    extend: {
      fontFamily: {
        nunito: ['Nunito, sans-serif'],
      },
      colors: {
        primary: {
          DEFAULT: '#008849',
          hover: '#006A38',
          active: '#005229',
          focus: '#008849',
          disabled: '#D1D5DB',
          dark: '#00371a',
          vibrant: '#009966',
        },
        secondary: {
          DEFAULT: '#212B36',
          button: '#F3F4F6',
          hover: '#E5E7EB',
          active: '#D1D5DB',
          focus: '#F3F4F6',
          disabled: '#F9FAFB',
        },
        table: {
          default: '#FFFFFF',
          rowSelect: '#ECFDF3',
        },
        pipblue: {
          DEFAULT: '#2E90FA',
        },
        pipgrey: {
          DEFAULT: '#737373',
          md: '#C4C4C4',
          light: '#E7E7E7',
          dark: '#2D2D2D',
          border: '#EAECF0',
          disabled: '#D0D5DD',
        },
        pipstatus: {
          default: '#202020',
          info: '#3B82F6',
          'info-light': '#B8D9FF',
          success: '#10B981',
          warning: '#F59E0B',
          error: '#E40000',
          alert: '#D92D20',
        },
        piptext: {
          default: '#212B36',
          body: '#6B7280',
          strong: '#374151',
          maximum: '#1F2937',
          blue: '#175CD3',
          error: '#C50214',
          chart: '#212b36',
        },

        pipbg: {
          DEFAULT: '#FFFFFF',
          subtle: '#F5F5F5',
          grouped: '#E5E5E5',
          pressed: '#C9CADD',
          'hover-red': '#b70000',
        },

        pippink: {
          100: '#FDECF8', // fill
          200: '#FCDAF2', // border
          600: '#EE46BC', // dot
          900: '#8F2A71', // text
        },
        pipgreen: {
          100: '#E7F8F2', // fill
          200: '#CFF1E6', // border
          600: '#10B981', // dot
          900: '#0A6F4D', // text
        },
        pippurple: {
          100: '#F0ECF9', // fill
          200: '#E1D9F4', // border
          600: '#6941C6', // dot
          900: '#3F2777', // text
        },

        pieChart: {
          slice1: '#F5F5F5', // light grey
          slice2: '#c9cadd', // grey-300
          slice3: '#6b7280', // grey-500
          slice4: '#374151', // grey-700
          slice5: '#e5e5e5', // grey-200
          slice6: '#101828', // grey-900
          slice7: '#667085', // grey-500 variant
        },
      },
      boxShadow: {
        sideShadow: '4px 4px 8px rgba(0, 0, 0, 0.15)',
        outlinedBottomShadow: '0 0 1px 0 rgba(0, 0, 0, 0.12), 0 6px 6px -6px rgba(0, 0, 0, 0.08)',
      },
      screens: {
        'pip-xl': '1287px',
      },
      transitionDuration: {
        'state-change': '300ms',
      },
    },
  },
  plugins: [
    plugin((api) => {
      api.addComponents({
        '.chip': {
          '@apply inline-flex text-base border font-normal rounded-lg px-2 w-fit': {},
        },
        '.small-chip': {
          '@apply inline-flex text-xs border font-normal rounded-lg px-2 w-fit max-w-[100px]': {},
        },
        '.chip-default': {
          '@apply text-secondary bg-gray-100 border-gray-300': {},
        },
        '.chip-green': {
          '@apply text-primary bg-emerald-50 border-emerald-300': {},
        },
        '.chip-blue': {
          '@apply text-pipstatus-info bg-sky-50 border-sky-300': {},
        },
        '.chip-red': {
          '@apply text-piptext-error bg-rose-50 border-rose-300': {},
        },
        '.chip-purple': {
          '@apply text-violet-600 bg-violet-50 border-violet-300': {},
        },
        '.chip-pippink': {
          '@apply text-pippink-900 bg-pippink-100 border-pippink-200': {},
        },
        '.chip-pipgreen': {
          '@apply text-pipgreen-900 bg-pipgreen-100 border-pipgreen-200': {},
        },
        '.chip-pippurple': {
          '@apply text-pippurple-900 bg-pippurple-100 border-pippurple-200': {},
        },
        '.chip-yellow': {
          '@apply text-yellow-700 bg-yellow-50 border-yellow-300': {},
        },
        '.variance-green': {
          '@apply text-primary': {},
          background: '#E0F5EB',
        },

        '.button-base': {
          '@apply font-nunito rounded-lg text-sm font-semibold leading-[14px] flex justify-center items-center gap-2 cursor-pointer': {},
          padding: '12px 14px',
        },
        '.button-radio': {
          '@apply font-nunito rounded-lg text-sm font-normal leading-[14px] flex justify-start items-center gap-2 py-2.75  px-4 cursor-pointer': {},
        },
        '.button-default': {
          '@apply text-secondary border hover:bg-secondary-hover hover:border-pipgrey-md active:bg-secondary-active focus:ring-secondary-focus': {},
        },
        '.button-red': {
          '@apply border bg-pipstatus-alert text-white hover:bg-pipbg-hover-red': {},
        },
        '.button-green': {
          '@apply text-white border bg-primary hover:bg-primary-hover active:bg-primary-active focus:ring-primary-focus': {},
          '@apply disabled:bg-primary-disabled disabled:border-primary-disabled disabled:text-piptext-body disabled:cursor-not-allowed': {},
        },
        '.button-error-outline': {
          '@apply button-base border border-pipstatus-error text-pipstatus-error transition-colors': {},
          '&:hover': {
            '@apply bg-pipstatus-error text-white': {},
          },
        },
        '.button-error-solid': {
          '@apply button-base border border-pipstatus-error bg-pipstatus-error text-white': {},
        },
        // Typography System
        '.text-page-title': {
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontSize: '40px',
          fontWeight: '600',
          lineHeight: '38.4px',
          letterSpacing: '0',
        },
        '.text-body-large': {
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '24px',
          letterSpacing: '0',
        },
        '.text-body-regular': {
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '21px',
          letterSpacing: '0',
        },
        '.text-body-small': {
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: '12px',
          fontWeight: '400',
          lineHeight: '16.8px',
          letterSpacing: '0',
        },
        '.text-button': {
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: '14px',
          fontWeight: '600',
          lineHeight: '14px',
          letterSpacing: '0',
        },
        '.text-label': {
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: '12px',
          fontWeight: '600',
          lineHeight: '14.4px',
          letterSpacing: '0',
        },
        '.text-helper': {
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: '12px',
          fontWeight: '400',
          lineHeight: '16.8px',
          letterSpacing: '0',
        },
        '.text-frame': {
          '@apply relative mx-auto w-full text-center text-xs font-semibold leading-5 text-[#344054]': {},
          '@apply before:absolute before:left-0 before:top-1/2 before:h-px before:w-[calc(50%-22px)] before:-translate-y-1/2 before:bg-[#D0D5DD]': {},
          '@apply after:absolute after:right-0 after:top-1/2 after:h-px after:w-[calc(50%-22px)] after:-translate-y-1/2 after:bg-[#D0D5DD]': {},
        },
        '.button-green-numerical': {
          '@apply min-w-9 text-base font-semibold underline text-primary transition-colors': {},
          '@apply hover:cursor-pointer hover:text-primary focus:outline-none focus:ring-2': {},
          '@apply focus:ring-primary/30': {},
        },
        '.input-base': {
          '@apply py-2 px-2.5 inline-flex w-full min-w-60 rounded-lg font-normal text-sm leading-[21px]': {},
        },
        '.input-label': {
          '@apply text-sm text-secondary font-semibold': {},
        },
        '.input-default': {
          '@apply input-base outline outline-gray-200': {},
        },
        '.input-focused': {
          '@apply input-base outline-none ring-1 ring-primary ': {},
        },
        '.input-error': {
          '@apply input-base outline outline-pipstatus-error': {},
        },
        '.input-success': {
          '@apply input-base outline outline-pipstatus-success': {},
        },
        '.input-disabled': {
          '@apply input-base bg-pipgrey-light text-piptext-body cursor-not-allowed': {},
        },
        '.input-readonly': {
          '@apply input-base bg-pipgrey-light': {},
        },

        '.focused-filter': {
          '@apply bg-pipstatus-success/20 animate-pulse shadow-lg ring-2 ring-primary/75': {},
        },
        '.search-filter-option': {
          '@apply p-2 text-sm font-thin bg-transparent hover:text-secondary hover:bg-pipbg-subtle': {},
        },
        '.modal-container': {
          '@apply flex flex-col items-center inset-0 m-auto p-4 border-0 rounded-md shadow-lg bg-white w-1/2 text-center backdrop:opacity-70 backdrop:bg-pipgrey-light': {},
        },
        '.react-aria-Button': {
          '@apply flex items-center justify-center rounded border-none m-1 w-[1.429rem] h-[1.429rem] text-xs box-content bg-pipbg-subtle text-piptext-body': {},
          color: '#737373',
          'forced-color-adjust': 'none',
        },
        '.react-aria-Button[data-pressed]': {
          '@apply shadow-none bg-pipgrey-md': {},
        },
        '.react-aria-Button[data-focus-visible]': {
          '@apply outline outline-2 outline-offset-2 ring-2 ring-primary ring-primary/50 ': {},
        },
        '.tooltip': {
          '@apply bg-primary-vibrant text-white cursor-help rounded-lg': {},
          boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
          padding: '4px 8px',
          maxWidth: '250px',
          transform: 'translate3d(0,0,0)',
          transition: 'transform 200ms, opacity 200ms',
        },
        '.tooltip-grey': {
          '@apply tooltip bg-pipbg-subtle p-3 shadow-outlinedBottomShadow text-body-large text-[15px] leading-[21px] text-piptext-default': {},
        },
        '.react-aria-Tooltip[data-placement=top]': {
          marginBottom: '10px',
          '--origin': 'translateY(4px)',
        },
        '.react-aria-Tooltip[data-placement=bottom]': {
          marginTop: '10px',
          '--origin': 'translateY(-4px)',
        },
        '.react-aria-Tooltip[data-placement=bottom] .react-aria-OverlayArrow svg': {
          transform: 'rotate(180deg)',
        },
        '.react-aria-Tooltip[data-placement=right]': {
          marginLeft: '10px',
          '--origin': 'translateX(-4px)',
        },
        '.react-aria-Tooltip[data-placement=right] .react-aria-OverlayArrow svg': {
          transform: 'rotate(90deg)',
        },
        '.react-aria-Tooltip[data-placement=left]': {
          marginRight: '10px',
          '--origin': 'translateX(4px)',
          transform: 'rotate(-90deg)',

        },
        '.card': {
          '@apply m-2 p-2 font-normal min-w-[150px] outline -outline-offset-1 outline-pipgrey-light shadow-[0_0_10px_rgba(0,0,0,0.1)] rounded-lg': {},
        },
        '.grid-container': {
          '@apply bg-white border border-pipgrey-border rounded-xl p-6 w-full flex flex-col gap-6': {},
        },
        '.grid-columns-2-3': {
          '@apply grid grid-cols-2 lg:grid-cols-3 gap-4 mt-2 pt-4 border-t border-pipgrey-border': {},
        },
        '.grid-tile-container': {
          '@apply flex items-stretch flex-1 min-w-0': {},
        },
        '.scrollbar-hover': {
          'scrollbar-gutter': 'stable',
          paddingRight: '4px',
        },
        '.scrollbar-hover::-webkit-scrollbar': {
          '@apply bg-transparent transition duration-200 w-2': {},
        },
        '.scrollbar-hover::-webkit-scrollbar-thumb': {
          '@apply bg-pipgrey-dark/30 rounded-lg transition duration-200': {},
        },
        '.scrollbar-hover:hover::-webkit-scrollbar-thumb': {
          '@apply bg-pipgrey-dark': {},
        },
        '.scrollbar-hover::-webkit-scrollbar-thumb:hover': {
          '@apply bg-primary cursor-pointer': {},
        },

      });
    }),
    plugin((api) => {
      const baseThumbStyles = {
        'background-color': '#f9fafb',
        'border-radius': '50%',
        cursor: 'pointer',
        height: '14px',
        width: '14px',
        'margin-top': '4px',
        'pointer-events': 'auto',
        position: 'relative',
      };

      api.addBase({
        h1: {
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontSize: '32px',
          fontWeight: '600',
          lineHeight: '38.4px',
          letterSpacing: '0',
        },
        h2: {
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontSize: '24px',
          fontWeight: '600',
          lineHeight: '31.2px',
          letterSpacing: '0',
        },
        h3: {
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontSize: '20px',
          fontWeight: '600',
          lineHeight: '26px',
          letterSpacing: '0',
        },
        h4: {
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontSize: '16px',
          fontWeight: '600',
          lineHeight: '22.4px',
          letterSpacing: '0',
        },
        'input[type="search"]::-webkit-search-cancel-button': {
          '-webkit-appearance': 'none',
          height: '16px',
          width: '16px',
          marginLeft: '0.2em',
          backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'><circle cx=\'12\' cy=\'12\' r=\'10\' fill=\'transparent\' stroke=\'%23FF6259\' stroke-width=\'1.5\'/><path d=\'M8 8L16 16M8 16L16 8\' stroke=\'%23FF6259\' stroke-width=\'1.5\' stroke-linecap=\'round\'/></svg>")',
          cursor: 'pointer',
        },
        'input[type="search"]::-webkit-search-cancel-button:hover': {
          opacity: '1',
        },
        'input[type="range"]::-moz-range-thumb': {
          ...baseThumbStyles,
          'border-color': '#008849',
          'box-shadow': '0 1px 2px 0 #0000000d #008849',
        },
        'input[type="range"]::-webkit-slider-thumb': {
          ...baseThumbStyles,
          '-webkit-appearance': 'none',
          appearance: 'none',
          'box-shadow': '0 1px 2px 0 #0000000d, 0 0 0 2px #008849',
        },
        'input.dual-range-overlap-min[type="range"]::-moz-range-thumb': {
          transform: 'translateX(-2.5px)',
        },
        'input.dual-range-overlap-max[type="range"]::-moz-range-thumb': {
          transform: 'translateX(2.5px)',
        },
        'input.dual-range-overlap-min[type="range"]::-webkit-slider-thumb': {
          transform: 'translateX(-2.5px)',
        },
        'input.dual-range-overlap-max[type="range"]::-webkit-slider-thumb': {
          transform: 'translateX(2.5px)',
        },
        'input[type="range"]:focus::-moz-range-thumb': {
          outline: 'none',
          'background-color': '#ffffff',
          'box-shadow': '0 1px 2px 0 #2020201a, 0 0 0 2px #0088494d',
        },
        'input[type="range"]:focus::-webkit-slider-thumb': {
          '-webkit-appearance': 'none',
          appearance: 'none',
          'background-color': '#ffffff',
          'box-shadow': '0 1px 2px 0 #2020201a, 0 0 0 2px #00a855, 0 0 0 4px #0088494d',
        },
        '.benefits-markdown a': {
          '@apply underline hover:text-primary transition-colors': {},
        },
      });
    }),
  ],
} satisfies Config;
